import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import Btn from '../../../../../../components/Buttons/Btn';
import Modal from '../../../../../../components/Modal';
import {
  ApiResPaginated,
  Role,
  Seller,
  SupervisedSeller,
  User,
} from '../../../../../../types';

import {
  isValidEmail,
  isValidPassword,
  isValidPasswordConfirmation,
  isValidUserName,
  isValidUsername,
} from '../../../../../../utils/validations';

import AsyncTable, { Column, RequestPage } from '../../../../../../components/AsyncTable';
import Input from '../../../../../../components/Input';
import MultiSelect from '../../../../../../components/MultiSelect';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SingleSelect from '../../../../../../components/SingleSelect';
import {
  ActionButtonsContainer,
  DeleteIconTable,
} from '../../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../../enums/httpStatus';
import {
  addSellersForSupervision,
  getSupervisedSellers,
  removeSellersFromSupervision,
  rolesReq,
  sellersApi,
  usersReq,
} from '../../../../../../services/requests';
import {
  SelectOption,
  getOptionsFromRoles,
  getOptionsFromSellers,
} from '../../../../../../utils/getSelectOptions';
import { roleToSelectOption } from '../../../../../../utils/itensToSelectOption';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  callback: () => void;
  userSelected?: User | null;
};

export const CreateOrEditUserModal = ({
  showModal,
  closeModal,
  callback,
  userSelected,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isEditMode = Boolean(userSelected?.id);
  const isTenantAdmin = userSelected?.is_tenant_admin ?? false;

  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [rolesSelected, setRolesSelected] = useState<SelectOption[]>([]);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Supervised sellers states
  const [supervisedSellers, setSupervisedSellers] = useState<SupervisedSeller[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectSellers, setSelectSellers] = useState<SelectOption[]>([]);
  const [updateTable, setUpdateTable] = useState(0);

  // Linked seller states
  const [isLinkedWithSeller, setIsLinkedWithSeller] = useState(false);
  const [linkedSellerSelected, setLinkedSellerSelected] = useState<SelectOption | null>(null);
  const [sellersOptions, setSellersOptions] = useState<SelectOption[]>([]);

  // For new user, we need to store supervision sellers to add after creation
  const [pendingSupervisionSellers, setPendingSupervisionSellers] = useState<SelectOption[]>([]);

  const getRolesReq = useCallback(async () => {
    const source = rolesReq.axios.CancelToken.source();

    try {
      const response = await rolesReq.index(source.token);

      if (response.status === HttpStatus.OK) {
        const rolesData = Array.isArray(response.data.data)
          ? response.data.data
          : response.data;
        setRoles(rolesData);
      } else {
        throw response;
      }
    } catch (err) {
      if (!rolesReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  }, []);

  const getSellersToFilterOptions = async () => {
    const source = sellersApi.axios.CancelToken.source();

    try {
      sellersApi.query = '?&quantityPerPage=100000';
      const res = await sellersApi.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      setSellers(res.data.data);
      setSellersOptions(getOptionsFromSellers(res.data.data));
    } catch (err) {
      if (!sellersApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }
  };

  const loadInitialData = async () => {
    setIsLoading(true);
    await Promise.all([getRolesReq(), getSellersToFilterOptions()]);
    setIsLoading(false);
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const userRolesToSelectOptions = (ro: Role[]) =>
    ro.map((roleItem) => roleToSelectOption(roleItem));

  // Load user data when editing
  useEffect(() => {
    if (userSelected) {
      setName(userSelected.name);
      setEmail(userSelected.email ?? '');
      setUsername(userSelected.username ?? '');
      setRolesSelected(
        userSelected.roles ? userRolesToSelectOptions(userSelected.roles) : []
      );

      // Set linked seller
      if (userSelected.is_seller) {
        setIsLinkedWithSeller(true);
        const sellerOption = sellersOptions.find(
          (opt) => opt.value === userSelected.is_seller
        );
        if (sellerOption) {
          setLinkedSellerSelected(sellerOption);
        } else {
          setLinkedSellerSelected({
            value: userSelected.is_seller,
            label: userSelected.is_seller,
          });
        }
      } else {
        setIsLinkedWithSeller(false);
        setLinkedSellerSelected(null);
      }
    } else {
      // Reset form for new user
      setName('');
      setEmail('');
      setUsername('');
      setPassword('');
      setPasswordConfirm('');
      setRolesSelected([]);
      setIsLinkedWithSeller(false);
      setLinkedSellerSelected(null);
      setPendingSupervisionSellers([]);
    }
  }, [userSelected, sellersOptions]);

  const notifyValidationError = (message: string): false => {
    frontendNotification({
      message,
      type: 'warning',
    });

    return false;
  };

  const validateForm = (): boolean => {
    const normalizedName = name.trim();
    const normalizedUsername = username.trim();
    const normalizedEmail = email.trim();

    if (!normalizedName) {
      return notifyValidationError(t('nameIsRequired'));
    }

    if (!isValidUserName(normalizedName)) {
      return notifyValidationError(t('invalidName'));
    }

    if (!normalizedUsername) {
      return notifyValidationError(t('usernameIsRequired'));
    }

    if (!isValidUsername(normalizedUsername)) {
      return notifyValidationError(t('invalidUsername'));
    }

    if (!normalizedEmail) {
      return notifyValidationError(t('emailRequired'));
    }

    if (
      normalizedEmail.length < 5
      || normalizedEmail.length > 50
      || !isValidEmail(normalizedEmail)
    ) {
      return notifyValidationError(t('invalidEmail'));
    }

    if (!isTenantAdmin && rolesSelected.length === 0) {
      return notifyValidationError(t('permissionGroupIsRequired'));
    }

    if (!isEditMode) {
      if (!password) {
        return notifyValidationError(t('passwordIsRequired'));
      }

      if (!isValidPassword(password)) {
        return notifyValidationError(t('invalidPassword'));
      }

      if (!passwordConfirm) {
        return notifyValidationError(t('passwordConfirmationIsRequired'));
      }

      if (!isValidPasswordConfirmation(password, passwordConfirm)) {
        return notifyValidationError(t('passwordsDoNotMatch'));
      }
    }

    return true;
  };

  const saveBtnAction = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const data: any = {
        name,
        username,
        roles: rolesSelected.map((e) => e.value),
        email,
      };

      // Handle password - only for new user
      if (!isEditMode) {
        data.password = password;
        data.password_confirmation = passwordConfirm;
      }

      // Handle linked seller
      if (isLinkedWithSeller && linkedSellerSelected) {
        data.is_seller = linkedSellerSelected.value;
      } else {
        data.is_seller = null;
      }

      // Handle supervision sellers for new user
      if (!isEditMode && pendingSupervisionSellers.length > 0) {
        data.supervised_sellers = pendingSupervisionSellers.map((s) => s.value);
      }

      usersReq.query = '';
      let response;

      if (isEditMode && userSelected) {
        response = await usersReq.update(data, userSelected.id);
      } else {
        response = await usersReq.store(data);
      }

      if (
        response.status === HttpStatus.OK ||
        response.status === HttpStatus.CREATED
      ) {
        frontendNotification({
          message: isEditMode ? t('updatedUser') : t('registeredUser'),
          type: 'success',
        });

        callback();
        closeModal();
      } else {
        throw response;
      }

      setIsLoading(false);
    } catch (err) {
      if (!rolesReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setIsLoading(false);
    }
  };

  // Supervision sellers management (only for edit mode)
  const handleTableDeleteBtn = async (row: SupervisedSeller) => {
    if (!userSelected) {
      setPendingSupervisionSellers(
        pendingSupervisionSellers.filter((s) => s.value !== row.sellerCode)
      );
      setUpdateTable(updateTable + 1);
      return;
    }

    setIsLoading(true);

    try {
      const res = await removeSellersFromSupervision.store({
        userId: userSelected.id,
        sellers: [row.sellerCode],
      });

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      frontendNotification({
        message: t('removedSellers'),
        type: 'success',
      });

      setUpdateTable(updateTable + 1);
    } catch (err) {
      if (!sellersApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  const actionsButtons = (row: SupervisedSeller) => (
    <ActionButtonsContainer>
      <DeleteIconTable
        style={
          row.sellerCode === userSelected?.is_seller ? { display: 'none' } : {}
        }
        onClick={() => handleTableDeleteBtn(row)}
      />
    </ActionButtonsContainer>
  );

  const columns: Column<SupervisedSeller>[] = [
    {
      label: t('sellerCode') as string,
      accessor: 'sellerCode',
    },
    {
      label: t('name') as string,
      accessor: 'name',
    },
    {
      Cell: actionsButtons,
    },
  ];

  const getItems: RequestPage = async ({ search, page, quantityPerPage }) => {
    if (!userSelected) {
      const formattedData = pendingSupervisionSellers.map((s) => ({
        sellerCode: String(s.value),
        name: s.label,
      }));

      const filtered = search
        ? formattedData.filter(
          (item) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            String(item.sellerCode).includes(search)
        )
        : formattedData;

      const start = (page - 1) * quantityPerPage;
      const end = start + quantityPerPage;
      const pageData = filtered.slice(start, end);

      const totalPage = Math.ceil(filtered.length / quantityPerPage);
      const hasMore = page < totalPage;

      return {
        data: pageData,
        hasMore,
        totalPage,
      };
    }

    let hasMore = true;
    const source = getSupervisedSellers.axios.CancelToken.source();

    try {
      const userId = userSelected.id;
      const queryParams = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      getSupervisedSellers.query = `/${userId}${queryParams}`;
      const res = await getSupervisedSellers.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<SupervisedSeller[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!getSupervisedSellers.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const addSellersForSupervisionReq = async () => {
    if (!userSelected) {
      // For new user, just add to pending list
      setPendingSupervisionSellers([
        ...pendingSupervisionSellers,
        ...selectSellers,
      ]);
      setSelectSellers([]);
      setUpdateTable(updateTable + 1);
      return;
    }

    setIsLoading(true);

    try {
      const res = await addSellersForSupervision.store({
        userId: userSelected.id,
        sellers: selectSellers.map((sellerOption) => sellerOption.value),
      });

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      frontendNotification({
        message: t('addedSellers'),
        type: 'success',
      });

      setSelectSellers([]);
      setUpdateTable(updateTable + 1);
    } catch (err) {
      if (!addSellersForSupervision.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };
  return (
    <Modal
      title={isEditMode ? t('editUser') : t('registerUser')}
      showModal={showModal}
      closeModal={closeModal}
      action={() => saveBtnAction()}
      isLoading={isLoading}
      bodyStyle={{ overflow: 'scroll', minWidth: '65vw' }}
    >
      <>
        <form>
          {/* Row 1: Name, Username, Email, Permission Groups */}
          <div className="row">
            <div className="itemFormWithLabel">
              <Input
                value={name}
                onChange={(e: any) => setName(e.target.value)}
                label={t('name')}
                required={t('required')}
                disabled={isLoading}
              />
            </div>
            <div className="itemFormWithLabel">
              <Input
                value={username}
                onChange={(e: any) => setUsername(e.target.value)}
                label={`${t('loginUsername')} (username)`}
                type="text"
                errorMsg={t('invalidUsername')}
                required={t('required')}
                disabled={isLoading}
              />
            </div>
            <div className="itemFormWithLabel">
              <Input
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                label="Email"
                type="email"
                errorMsg={t('invalidEmail')}
                required={t('required')}
                disabled={isLoading}
              />
            </div>
            <div className="itemFormWithLabel">
              {isTenantAdmin ? (
                <Input
                  value={t('admin')}
                  onChange={() => null}
                  type="text"
                  disabled={isLoading}
                  label={t('role')}
                />
              ) : (
                <MultiSelect
                  options={getOptionsFromRoles(roles)}
                  value={isLoading ? [] : rolesSelected}
                  onChange={setRolesSelected}
                  placeholder={
                    isLoading ? t('loading') : t('selectPermissionGroups')
                  }
                  disabled={isLoading}
                  label={t('selectPermissionGroups')}
                />
              )}
            </div>
          </div>

          {/* Row 2: Password - Only for new user */}
          {!isEditMode && (
            <div className="row">
              <div className="itemFormWithLabel">
                <Input
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  label={t('password')}
                  type="password"
                  showPswBtn
                  validator={isValidPassword}
                  errorMsg={t('invalidPassword')}
                  required={t('required')}
                  disabled={isLoading}
                />
              </div>
              <div className="itemFormWithLabel">
                <Input
                  value={passwordConfirm}
                  onChange={(e: any) => setPasswordConfirm(e.target.value)}
                  label={t('confirmPassword')}
                  type="password"
                  showPswBtn
                  isMatch={password}
                  errorMsg={t('passwordsDoNotMatch')}
                  required={t('required')}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Row 3: Linked Seller */}
          <div className="row">
            <div
              className="itemFormWithLabel"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                }}
              >
                <label style={{ marginRight: '0.5rem' }}>
                  {t('linkWithSeller')}
                </label>
                <input
                  onChange={(e: any) => {
                    setIsLinkedWithSeller(e.target.checked);
                    if (!e.target.checked) {
                      setLinkedSellerSelected(null);
                    }
                  }}
                  type="checkbox"
                  checked={isLinkedWithSeller}
                  disabled={isLoading}
                />
              </div>
              {isLinkedWithSeller && (
                <SingleSelect
                  options={sellersOptions}
                  value={linkedSellerSelected}
                  onChange={(e: any) => setLinkedSellerSelected(e)}
                  isDisabled={isLoading}
                  placeholder={isLoading ? t('loading') : t('selectSeller')}
                />
              )}
            </div>
          </div>
        </form>

        {/* Separator */}
        <hr
          style={{
            margin: '1.5rem 0',
            border: 'none',
            borderTop: `1px solid ${theme.colors.onSurfaceLight}`,
          }}
        />

        {/* Supervised Sellers Section */}
        <hr className='division op2' />
        <div className="itemFormWithLabel">
          <h3 className='sm-title'>
            {t('supervisedSellers')}
          </h3>
        </div>

        <div className="row">
          <div className="itemFormWithLabel">
            <label htmlFor="selectSellers">{t('selectSellers')}</label>
            <MultiSelect
              id="selectSellers"
              labelledBy="sellers-list"
              options={getOptionsFromSellers(sellers)}
              value={selectSellers}
              onChange={setSelectSellers}
              placeholder={isLoading ? t('loading') : t('filterSellers')}
              disabled={isLoading}
            />
          </div>
          <div className="itemForm">
            <Btn
              text={t('addSellersForSupervision')}
              onClick={() => addSellersForSupervisionReq()}
              disabled={selectSellers.length === 0}
            />
          </div>
        </div>

        {/* Show supervised sellers table for edit mode */}
        <div className="row">
          <div className="itemFormWithLabel">
            <AsyncTable
              tableName={t('supervisedSellersTable')}
              columns={columns}
              value={supervisedSellers}
              onChange={setSupervisedSellers}
              requestPage={getItems}
              reqListener={[updateTable]}
              id="supervisedSellersTable"
              defaultQuantityPerPage={5}
              options={{
                styles: {
                  primaryColor: `${theme.colors.surface}`,
                  secondaryColor: `${theme.colors.onSurface}`,
                  alternateRowColor: theme.colors.onSurfaceLight,
                  textColor: theme.colors.text,
                },
                quantityPerPageLabel: t('quantityPerPage'),
                searchPlaceholder: t('search'),
                totalPageLabel: `${t('totalPages')}: `,
              }}
            />
          </div>
        </div>
      </>
    </Modal>
  );
};

export default CreateOrEditUserModal;
